/**
 * Accept an invitation to a group.
 *
 * This screen used to create the account *and* join the group, because one code
 * did both. It does not any more: a group invitation admits an existing account
 * to a group and cannot bring an account into being. So the page has two
 * shapes — join it, when there is somebody to join it as, and otherwise send
 * the person to make an account and come back.
 *
 * The code is checked before anything is asked of the reader, so the group's
 * name can be shown first. Being asked to sign up for an unnamed thing is a
 * poor first impression, and being asked twice because the code turned out to
 * be expired is worse.
 */

import { useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { trpc, errorMessage } from "../../lib/trpc.js";
import { useAuth } from "../../lib/auth.js";
import { Button } from "../../components/ui/Button.js";
import { Logo } from "../../components/ui/Logo.js";
import { Skeleton } from "../../components/ui/Skeleton.js";
import { joinGroupWithCode } from "../group/joinGroup.js";
import "./auth.css";
import { EmojiIcon } from "../../components/ui/EmojiIcon.js";

export function JoinPage() {
  const { code } = useParams({ from: "/join/$code" });
  const auth = useAuth();
  const navigate = useNavigate();

  const invite = useQuery(trpc.auth.inspectInvite.queryOptions({ code }));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (invite.isLoading) {
    return (
      <div className="fc-auth">
        <div className="fc-auth__card">
          <Skeleton height="28px" width="60%" />
          <Skeleton height="16px" width="80%" />
          <Skeleton height="36px" />
        </div>
      </div>
    );
  }

  if (!invite.data?.valid) {
    return (
      <div className="fc-auth">
        <div className="fc-auth__card">
          <Logo size={28} withWordmark className="fc-auth__logo" />
          <h1 className="fc-auth__title">This invite isn&rsquo;t valid</h1>
          <p className="fc-auth__tagline">
            It may have expired, been used up, or been withdrawn. Ask whoever
            sent it for a fresh one.
          </p>
          <Button
            variant="secondary"
            onClick={() =>
              void navigate({ to: "/login", search: { redirect: undefined } })
            }
          >
            Go to sign in
          </Button>
        </div>
      </div>
    );
  }

  const group = invite.data.group;

  async function join() {
    setError(null);
    setBusy(true);
    try {
      const { keyAdopted } = await joinGroupWithCode(code);
      if (!keyAdopted) {
        setError(
          "You are in the group, but this browser could not get its key. " +
            "Ask someone already in it to open the group once.",
        );
        return;
      }
      await navigate({ to: "/" });
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fc-auth">
      <div className="fc-auth__card">
        <Logo size={28} withWordmark className="fc-auth__logo" />

        <h1 className="fc-auth__title">
          {group.emoji && (
            <span aria-hidden="true">
              <EmojiIcon emoji={group.emoji} />{" "}
            </span>
          )}
          {group.name}
        </h1>
        <p className="fc-auth__tagline">
          You&rsquo;ve been invited to this group. Everything shared in it is
          visible only to the people in it.
        </p>

        {error && (
          <div className="fc-auth__error" role="alert">
            {error}
          </div>
        )}

        {auth.status === "authenticated" ? (
          <Button onClick={() => void join()} loading={busy}>
            Join {group.name}
          </Button>
        ) : (
          <>
            {/*
              Nothing is asked for here. An invitation to a group says nothing
              about whether this person may hold an account, so it cannot make
              one — they sign in, or register, and the link brings them back.
            */}
            <p className="fc-auth__tagline">
              Sign in to accept it, or create an account first.
            </p>
            <Button
              onClick={() =>
                void navigate({
                  to: "/login",
                  search: { redirect: `/join/${code}` },
                })
              }
            >
              Sign in
            </Button>
            <Button
              variant="secondary"
              onClick={() =>
                void navigate({ to: "/register", search: { code: undefined } })
              }
            >
              Create an account
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
